<script setup lang="ts">
import { ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toIsoDate, formatEventDate } from '../../utils/eventDate';

const { t } = useI18n();

/**
 * One label/value row with click-to-edit inline editing, used by the
 * entity/event profile dialogs. Display mode shows the value (or an "+ Add"
 * affordance when empty); clicking enters edit mode with a confirm (✓)
 * and discard (✕) control. Enter confirms, Esc/✕ discards. A `select`
 * field renders its display value as an entity pill; a `date` field edits
 * with a native date picker (ISO under the hood, friendly display).
 */

const props = withDefaults(
    defineProps<{
        label: string;
        value: string;
        editType?: 'text' | 'textarea' | 'select' | 'date';
        options?: string[];
        placeholder?: string;
        addLabel?: string;
        // Highlights the value as changed-from-baseline (soft tint).
        modified?: boolean;
        // Optional validator: return an error string to block the save,
        // or null/'' to allow it.
        validate?: (value: string) => string | null;
    }>(),
    {
        editType: 'text',
        options: () => [],
        placeholder: '',
        addLabel: undefined,
        modified: false,
        validate: undefined,
    },
);

const emit = defineEmits<{ (e: 'save', value: string): void }>();

const editing = ref(false);
const draft = ref('');
const error = ref<string | null>(null);
const inputEl = ref<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);

const startEdit = async () => {
    // A date field edits in ISO (the input's format); everything else edits
    // the raw value.
    draft.value = props.editType === 'date' ? toIsoDate(props.value) : props.value;
    error.value = null;
    editing.value = true;
    await nextTick();
    const el = inputEl.value;
    el?.focus();
    if (props.editType === 'select' || props.editType === 'date') {
        // Open the native picker right away. showPicker needs user activation,
        // which the originating click provides; guard for browsers that don't
        // support it (focus alone still works).
        const picker = el as HTMLSelectElement | HTMLInputElement | null;
        if (picker && typeof picker.showPicker === 'function') {
            try {
                picker.showPicker();
            } catch {
                /* not allowed / unsupported — fall back to focus only */
            }
        }
    } else if (el && 'select' in el) {
        (el as HTMLInputElement).select();
    }
};

// Value the draft is compared/saved against. For dates that's the ISO form so
// re-picking the same day isn't treated as a change.
const baseline = () => (props.editType === 'date' ? toIsoDate(props.value) : props.value);
const normalized = () =>
    props.editType === 'select' || props.editType === 'date' ? draft.value : draft.value.trim();

// Exposed so the parent dialog can detect unsaved edits before it lets
// the user navigate or close.
const isDirty = (): boolean => editing.value && normalized() !== baseline();

// Commit the in-progress edit. Returns false (and surfaces the error)
// when validation blocks the save; true when committed or nothing to do.
const commit = (): boolean => {
    if (!editing.value) return true;
    const next = normalized();
    if (props.validate) {
        const err = props.validate(next);
        if (err) {
            error.value = err;
            return false;
        }
    }
    editing.value = false;
    error.value = null;
    if (next !== baseline()) emit('save', next);
    return true;
};

const confirm = () => {
    commit();
};

const cancel = () => {
    editing.value = false;
    error.value = null;
};

// When focus leaves the editor entirely with no unsaved change, revert to
// view mode automatically. Keeps editing when dirty (so a stray click
// doesn't silently discard changes) and ignores focus moving to the
// confirm/discard buttons within the editor.
const onFocusOut = (event: FocusEvent) => {
    const container = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (next && container.contains(next)) return;
    if (!isDirty()) cancel();
};

defineExpose({ isDirty, commit, cancel });

const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
        // Shift+Enter inserts a newline in the textarea; plain Enter confirms.
        if (props.editType === 'textarea' && event.shiftKey) return;
        event.preventDefault();
        confirm();
    } else if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
    }
};

// Clear a stale validation error as soon as the user keeps typing.
watch(draft, () => {
    if (error.value) error.value = null;
});
</script>

<template>
    <div class="mc-field" :class="{ 'mc-field-editing': editing }">
        <div class="mc-field-label">{{ label }}</div>
        <div class="mc-field-value">
            <template v-if="editing">
                <div
                    class="mc-field-edit"
                    :class="{ 'mc-field-edit-multiline': editType === 'textarea' }"
                    @focusout="onFocusOut"
                >
                    <select
                        v-if="editType === 'select'"
                        ref="inputEl"
                        v-model="draft"
                        class="mc-field-control mc-cell-select"
                        @keydown="onKeydown"
                    >
                        <option v-for="opt in options" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                    <textarea
                        v-else-if="editType === 'textarea'"
                        ref="inputEl"
                        v-model="draft"
                        rows="3"
                        class="mc-field-control mc-field-textarea"
                        :placeholder="placeholder"
                        @keydown="onKeydown"
                    ></textarea>
                    <input
                        v-else-if="editType === 'date'"
                        ref="inputEl"
                        v-model="draft"
                        type="date"
                        class="mc-field-control"
                        @keydown="onKeydown"
                    />
                    <input
                        v-else
                        ref="inputEl"
                        v-model="draft"
                        type="text"
                        class="mc-field-control"
                        :placeholder="placeholder"
                        @keydown="onKeydown"
                    />
                    <button type="button" class="mc-field-confirm" :title="t('common.confirm')" @click="confirm">
                        <span class="material-icons" style="font-size: 16px">check</span>
                    </button>
                    <button type="button" class="mc-field-discard" :title="t('matterContext.field.discard')" @click="cancel">
                        <span class="material-icons" style="font-size: 16px">close</span>
                    </button>
                </div>
                <div v-if="error" class="mc-field-error">{{ error }}</div>
            </template>

            <template v-else>
                <div
                    v-if="value"
                    class="mc-field-display"
                    :class="{
                        'mc-field-display-select': editType === 'select',
                        'mc-field-display-multiline': editType === 'textarea',
                        'mc-field-modified': modified && editType !== 'select',
                    }"
                    role="button"
                    tabindex="0"
                    @click="startEdit"
                    @keydown.enter="startEdit"
                >
                    <span
                        v-if="editType === 'select'"
                        class="mc-entity-pill"
                        :class="{ 'mc-entity-pill-modified': modified }"
                        >{{ value }}</span
                    >
                    <template v-else-if="editType === 'date'">{{
                        formatEventDate(value)
                    }}</template>
                    <template v-else>{{ value }}</template>
                </div>
                <button
                    v-else
                    type="button"
                    class="mc-field-add"
                    :class="{ 'mc-field-modified': modified }"
                    @click="startEdit"
                >
                    <span class="material-icons" style="font-size: 16px">add_circle_outline</span>
                    <span>{{ addLabel ?? t('common.add') }}</span>
                </button>
            </template>
        </div>
    </div>
</template>
